import { ScoreCard, TrajectoryCards, TrajectoryCardsProps } from "@foodpilot/foods";
import { Box } from "@mui/material";
import { useTranslation } from "react-i18next";
import { ScoreValue } from "src/api/client-api/post/scoreSchema";
import { ActionPlanData } from "src/context/Actions/useActionPlanData";
import { useActionsContext } from "src/context/Actions/useActionsContext";
import { ScoreService } from "src/context/EntityBasedScore/ScoreService";

type ScoreOptions = {
    id: number;
    label: string;
    unit: string;
    group: string;
    scoreId: number;
    onClick: () => void;
};

export type ScoreComparisonCardProps = {
    selectedOption: ScoreOptions;
    allOptions: ScoreOptions[];

    position: TrajectoryCardsProps["position"];
    disableFooter?: boolean;
    precision?: number;
    actionPlanData: ActionPlanData;
};
export const ScoreComparisonCard = (props: ScoreComparisonCardProps) => {
    const { position, disableFooter = false, allOptions, selectedOption, precision = 0, actionPlanData } = props;
    const { t } = useTranslation();
    const { persistedOptions, actionPlanMetadata } = useActionsContext();

    const data = allOptions.map((score) => {
        return {
            ...score,
            onClick: () => {
                score.onClick();
            },
        };
    });

    const _startingPost = actionPlanData.metadata.startingPost;
    const startingPost = {
        label: _startingPost.scoreByLabel,
        score: ScoreService.normalizeScore(_startingPost.score, precision).value,
    };

    const targetWithoutPlanScore = ScoreService.normalizeScore(
        actionPlanData.metadata.scoresWithoutActionPost.lastItem?.score,
        precision,
    ).value;
    const targetWithoutPlan = {
        label: persistedOptions.withoutPlanName,
        score: targetWithoutPlanScore,
    };

    const targetWithPlanScore = ScoreService.normalizeScore(
        actionPlanData.metadata.scoresWithActionPost.lastItem?.score,
        precision,
    ).value;
    const targetWithPlan = {
        label: persistedOptions.withPlanName,
        score: targetWithPlanScore,
    };
    const goal = actionPlanData.metadata.goal?.value;
    const unit = selectedOption.unit;

    const cardData = formatData({
        position,
        isGrowthDisabled: actionPlanMetadata.isGrowthDisabled,
        steps: {
            startingPost,
            targetWithoutPlan,
            targetWithPlan,
        },
    });
    return (
        <Box sx={{ width: "100%" }}>
            <ScoreCard
                header={{
                    selectedItemId: selectedOption !== undefined ? selectedOption.id : 1,
                    options: data,
                }}
                disableFooter={disableFooter}
            >
                <TrajectoryCards
                    goal={goal ?? null}
                    unit={unit}
                    steps={{
                        start: cardData[0]?.score ?? null,
                        endWithoutAction: cardData[1]?.score,
                        endWithAction: cardData[2]?.score ?? null,
                    }}
                    textOptions={{
                        goalStep: t("component.goal.info"),
                        noGoalLabel: t("component.goal.no-goal"),
                        startingStep: cardData[0]?.label ?? "",
                        targetWithoutPlanStep: cardData[1]?.label ?? "",
                        targetWithPlanStep: cardData[2].label ?? "",
                    }}
                    position={position}
                    improvement={"decrease"}
                />
            </ScoreCard>
        </Box>
    );
};

type Step = {
    label: string;
    score: ScoreValue;
};

type Options = {
    position: TrajectoryCardsProps["position"];
    isGrowthDisabled: boolean;
    steps: {
        startingPost: Step;
        targetWithoutPlan: Step;
        targetWithPlan: Step;
    };
};
const formatData = (options: Options): [Step | null, Step | undefined, Step] => {
    const {
        position,
        isGrowthDisabled,
        steps: { startingPost, targetWithPlan, targetWithoutPlan },
    } = options;

    // This segregates the logic from 3 different post, to the logic for 1st, 2nd and 3rd step from UI Card
    if (position === "horizontal") {
        if (isGrowthDisabled) {
            return [startingPost, undefined, targetWithPlan];
        }

        return [startingPost, targetWithoutPlan, targetWithPlan];
    }

    if (position === "vertical") {
        // Seems like whatever if position is vertical, it'll always be 2 items
        // And the POSITION 1, 2 OUT OF 0, 1, 2.
        // So I need to put 1st item as 2nd position in case growth is Disabled
        if (isGrowthDisabled) {
            return [null, startingPost, targetWithPlan];
        }

        return [null, targetWithoutPlan, targetWithPlan];
    }

    return [startingPost, targetWithoutPlan, targetWithPlan];
};
